<template>
  <div class="modal fade" id="AddClientModal" tabindex="-1" role="dialog" ref="addClientModalRef"
    aria-labelledby="addClientModalTitle" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title" id="addClientModalTitle">{{ title }}</h4>
          <button class="btn-close py-0" type="button" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <Form ref="clientForm" @submit="addClient" :validation-schema="clientSchema">
            <div class="row">
              <div class="col-md-6 mb-3">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Nom du client <span class="text-danger">*</span>
                  </label>
                  <Field name="nomClient" type="text" class="form-control shadow-none fs-md-15 text-black"
                    placeholder="Entrer le nom" />
                  <ErrorMessage name="nomClient" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6 mb-3">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Prénom du client <span class="text-danger">*</span>
                  </label>
                  <Field name="prenomClient" type="text" class="form-control shadow-none fs-md-15 text-black"
                    placeholder="Entrer le prénom" />
                  <ErrorMessage name="prenomClient" class="text-danger" />
                </div>
              </div>
              <div class="col-md-12 mb-3">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Téléphone <span class="text-danger">*</span>
                  </label>
                  <vue-tel-input placeholder="Entrer le numéro de téléphone" v-model="telephone1"
                    defaultCountry="BJ" mode="international" @validate="validatePhone"
                    class="shadow-none fs-md-15 text-black py-2">
                  </vue-tel-input>
                  <div v-if="telephoneErreur" class="text-danger">Veuillez entrer un numéro correct</div>
                </div>
              </div>

              <div class="col-md-12 mt-3">
                <div class="d-flex align-items-center">
                  <button class="btn btn-success me-3" type="submit" :disabled="enCours">
                    Ajouter un client
                  </button>
                  <button class="btn btn-danger" type="button" data-bs-dismiss="modal">
                    <i class="fa fa-times lh-1 me-1 position-relative top-2"></i>Annuler
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, ref } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as Yup from 'yup';
import ApiService from '@/services/ApiService';
import { error, hideModal, success } from '@/utils/utils';
import 'vue3-tel-input/dist/vue3-tel-input.css';
import { VueTelInput } from 'vue3-tel-input';

export default defineComponent({
  name: "AddClientModal",
  components: { Form, Field, ErrorMessage, VueTelInput },
  emits: ["getclient", "close"],

  setup(props, { emit }) {
    const title = "Ajouter un client";
    const addClientModalRef = ref<null | HTMLElement>(null);
    const clientForm = ref<any>(null);
    const enCours = ref(false);

    // Mêmes champs que la page « Ajouter un client » : nom, prénom, téléphone
    const clientSchema = Yup.object().shape({
      nomClient: Yup.string().required("Le nom du client est obligatoire"),
      prenomClient: Yup.string().required("Le prénom du client est obligatoire"),
    });

    const telephone1 = ref('');

    // Dernier résultat envoyé par le composant téléphone (unique source de vérité)
    const resultatTel = ref<any>(null);

    const validatePhone = (phoneObject: any) => {
      resultatTel.value = phoneObject;
    };

    // Le numéro est valide si le composant l'a dit
    const numeroValide = computed(() => resultatTel.value?.valid === true);

    // Erreur seulement si des chiffres ont été saisis ET que le composant dit « invalide »
    const telephoneErreur = computed(() => {
      const saisie = String(resultatTel.value?.nationalNumber ?? '').replace(/\D/g, '');
      return saisie.length > 0 && !numeroValide.value;
    });

    const reinitialiser = () => {
      clientForm.value?.resetForm?.();
      telephone1.value = '';
      resultatTel.value = null;
      enCours.value = false;
    };

    const addClient = async (values: any, { resetForm }: any) => {
      if (!numeroValide.value) {
        return; // le message d'erreur s'affiche déjà sous le champ
      }

      enCours.value = true;
      ApiService.post('/clients', {
        nomClient: values.nomClient,
        prenomClient: values.prenomClient,
        telephone1: resultatTel.value.number, // ex. "+22997979797", sans espaces
      })
        .then(({ data }) => {
          if (data.code == 201) {
            success(data.message);
            resetForm();
            reinitialiser();
            hideModal(addClientModalRef.value);
            emit('close');
            emit('getclient', data.data); // le client créé est sélectionné dans la vente
          } else {
            enCours.value = false;
          }
        })
        .catch((err) => {
          enCours.value = false;
          error(err?.response?.data?.message ?? "Le client n'a pas pu être ajouté.");
        });
    };

    // Fermeture du modal (croix, Annuler, clic à l'extérieur) : on repart d'un formulaire vide
    onMounted(() => addClientModalRef.value?.addEventListener('hidden.bs.modal', reinitialiser));
    onBeforeUnmount(() => addClientModalRef.value?.removeEventListener('hidden.bs.modal', reinitialiser));

    return {
      title,
      clientSchema,
      addClient,
      clientForm,
      addClientModalRef,
      telephone1,
      telephoneErreur,
      validatePhone,
      enCours,
    };
  },
});
</script>